import {
  MAX_VERSIONS,
  describeVersions,
  versionCountOf,
  versionLabel,
  versionQuantities,
  versionsProblem,
  withVersionCount,
  withVersionSplit,
  type VersionSplitMode,
  type VersionsValue,
} from "@shared-core/domain/line-versions";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../app/components/primitives/radio-group";

/** What the backend refused on the version inputs, bound beside them. */
export interface VersionsControlErrors {
  count?: string;
  split?: string;
  quantities?: string;
}

// The buy box's version control (order to invoice doc 108 §3.1; the staff item page and One's forms have the same
// fields in their own shell): how many versions of this item, 1 to 26, and how the quantity is shared between them.
// One version is the default and shows nothing more; from two, Split evenly (the default; the share is shown) or
// Enter quantities (a box per version, adding up to the quantity). Versions are labelled A, B, C… and renamed later.
// A customer sees one price for the line, never a price per version. The even share and the sum are previews of the
// server's rule; its own refusals win when both speak.
export function VersionsControl({
  label,
  countRaw,
  value,
  quantity,
  quantityStep,
  errors = {},
  onCountChange,
  onValueChange,
}: {
  label: string;
  countRaw: string;
  value: VersionsValue;
  quantity: number;
  quantityStep: number;
  errors?: VersionsControlErrors;
  onCountChange: (raw: string, next: VersionsValue) => void;
  onValueChange: (next: VersionsValue) => void;
}) {
  const countError = countRawError(label, countRaw) ?? errors.count;
  const preview = versionsProblem(value, quantity, quantityStep);
  const quantitiesError = errors.quantities ?? preview?.message;
  const shares = versionQuantities(value, quantity, quantityStep);
  const sum = value.entered
    .slice(0, value.count)
    .reduce<number>((total, box) => total + (box ?? 0), 0);
  const setSplit = (split: VersionSplitMode) =>
    onValueChange(withVersionSplit(value, split, quantity, quantityStep));

  return (
    <div className="space-y-2" data-testid="versions-control">
      <div className="flex flex-wrap items-center gap-3">
        <Label htmlFor="versions-count" className="text-sm font-medium">
          {label}
        </Label>
        <Input
          id="versions-count"
          type="number"
          min={1}
          max={MAX_VERSIONS}
          step={1}
          value={countRaw}
          aria-invalid={countError ? true : undefined}
          aria-describedby={countError ? "versions-count-error" : undefined}
          onChange={(e) => {
            const raw = e.target.value;
            onCountChange(
              raw,
              withVersionCount(
                value,
                countRawError(label, raw) ? 1 : versionCountOf(raw),
              ),
            );
          }}
          className="w-24 tabular-nums"
        />
        {value.count < 2 && !countError && (
          <span className="text-xs text-muted-foreground">
            One version. Add more for the same item in different artwork.
          </span>
        )}
      </div>
      {countError && (
        <p id="versions-count-error" className="text-sm text-destructive">
          {countError}
        </p>
      )}

      {value.count >= 2 && !countRawError(label, countRaw) && (
        <div className="space-y-2">
          <RadioGroup
            aria-label="Split between versions"
            value={value.split}
            onValueChange={(next) => setSplit(next as VersionSplitMode)}
            className="flex flex-wrap gap-x-5 gap-y-1"
          >
            {(
              [
                ["even", "Split evenly"],
                ["entered", "Enter quantities"],
              ] as const
            ).map(([mode, text]) => (
              <Label
                key={mode}
                htmlFor={`versions-split-${mode}`}
                className="flex items-center gap-2 text-sm font-normal"
              >
                <RadioGroupItem value={mode} id={`versions-split-${mode}`} />
                {text}
              </Label>
            ))}
          </RadioGroup>
          {errors.split && (
            <p className="text-sm text-destructive">{errors.split}</p>
          )}

          {value.split === "even" && shares !== null && (
            <p className="text-sm tabular-nums" data-testid="versions-summary">
              {describeVersions(shares, "even")}
            </p>
          )}

          {value.split === "entered" && (
            <div
              role="group"
              aria-label="Quantity of each version"
              className="grid grid-cols-2 gap-x-4 gap-y-1.5"
            >
              {Array.from({ length: value.count }, (_, position) => (
                <Label
                  key={position}
                  className="flex items-center gap-2 text-sm font-normal"
                >
                  <span className="w-5 shrink-0 font-medium text-muted-foreground">
                    {versionLabel(position + 1)}
                  </span>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    value={value.entered[position] ?? ""}
                    aria-label={`Version ${versionLabel(position + 1)} quantity`}
                    aria-invalid={quantitiesError ? true : undefined}
                    aria-describedby={
                      quantitiesError ? "versions-quantities-error" : undefined
                    }
                    onChange={(e) => {
                      const text = e.target.value.trim();
                      const next = Array.from(
                        { length: value.count },
                        (_, i) => value.entered[i] ?? null,
                      );
                      next[position] = text === "" ? null : Number(text);
                      onValueChange({ ...value, entered: next });
                    }}
                    className="w-24 tabular-nums"
                  />
                </Label>
              ))}
            </div>
          )}

          {value.split === "entered" && !quantitiesError && (
            <p
              className="text-xs text-muted-foreground tabular-nums"
              data-testid="versions-sum"
            >
              {sum.toLocaleString("en-GB")} of{" "}
              {quantity.toLocaleString("en-GB")}
            </p>
          )}

          {quantitiesError && (
            <p
              id="versions-quantities-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {quantitiesError}
              {value.split === "even" && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setSplit("entered")}
                  >
                    Enter quantities
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function countRawError(label: string, raw: string): string | undefined {
  const text = raw.trim();
  if (text === "") return undefined;
  if (!/^-?\d+$/.test(text)) return `${label} must be a whole number`;
  const count = Number(text);
  if (count < 1) return `${label} ${count} is below the minimum of 1`;
  if (count > MAX_VERSIONS)
    return `${label} ${count} exceeds the maximum of ${MAX_VERSIONS}`;
  return undefined;
}
